type SectionMap = {
  id: string;
  title: string;
  fields?: string[];
  attributes?: string[];
  prefixes?: string[];
};

// Keep request intent separate from resolved runtime values, even when names match.
const SECTION_MAP: SectionMap[] = [
  {
    id: 'identity',
    title: 'Identity and status',
    fields: [
      'id',
      'parentId',
      'name',
      'description',
      'taskType',
      'status',
      'createdAt',
      'updatedAt',
      'completedAt',
      'abortedAt',
      'deletedAt',
    ],
  },
  {
    id: 'placement',
    title: 'Placement',
    fields: [
      'requestedRegionPrefix',
      'region',
      'cloudEnvironment',
      'project',
      'bucket',
    ],
  },
  {
    id: 'request',
    title: 'Request inputs',
    attributes: [
      'samples',
      'outputPath',
      'inputBucketName',
      'stubRun',
      'withTrace',
      'withReport',
      'withTimeline',
      'maxRuntimeSeconds',
      'maxConcurrentNextflowDrivers',
      'processResultsPollIntervalSeconds',
      'machineType',
      'vmLifecycle',
      'version',
      'wasabiTransfer',
      'autoAnnotate',
      'createProject',
      'reconstructData',
      'deliveryProfile',
      'barcode_chemistry',
      'barcode_whitelist',
      'humanReferenceH5ad',
      'whitelistBc1',
      'whitelistBc2',
      'whitelistBc3',
      'startPhase',
      'endPhase',
      'ftpCmd',
    ],
    prefixes: ['newBucket'],
  },
  {
    id: 'release',
    title: 'Release selection',
    attributes: [
      'nextflowVersion',
      'profiles',
      'nativeConfig',
      'executor',
      'nextflowSampleFanout',
      'collectProcessResults',
      'executionFingerprintRequired',
      'artifactDelivery',
    ],
    prefixes: ['pipeline', 'workflow'],
  },
  {
    id: 'params',
    title: 'Nextflow params',
    attributes: ['params', 'paramsJson'],
  },
  {
    id: 'runtime',
    title: 'Runtime and cloud',
    attributes: [
      'gcpRegion',
      'googleLocation',
      'googleProject',
      'awsRegion',
      'awsQueue',
      'nfBucket',
      'awsS3WorkDir',
      'cloudEnvironmentId',
      'inputBucket',
      'inputBucketId',
      'outputBucket',
      'bucketName',
      'bucketInput',
      'bucketOutput',
      'outputBucketId',
      'outputBucketName',
      'requestedRegionPrefix',
    ],
  },
  {
    id: 'orchestration',
    title: 'Orchestration internals',
    attributes: [
      'orchestrated',
      'runLocal',
      'is_async',
      'dependsOn',
      'gceRequestId',
      'gceZone',
      'gceZones',
      'instanceName',
      'sourceImage',
      'metadata',
      'launchResults',
      'plan',
    ],
  },
  { id: 'stage-output', title: 'stageOutput', attributes: ['stageOutput'] },
  { id: 'other', title: 'Other' },
  { id: 'results', title: 'Results', fields: ['results'] },
  { id: 'subtasks', title: 'Subtasks', fields: ['subtasks'] },
];

export type DebugSection = {
  id: string;
  title: string;
  content: Record<string, unknown>;
};

function sortContent(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortContent);
  if (value === null || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => key !== '__typename')
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, child]) => [key, sortContent(child)])
  );
}

/** Present a GraphQL task without mutating it or interpreting date strings. */
export function computeTaskDebugSections(task: object): DebugSection[] {
  const sections = SECTION_MAP.map(({ id, title }) => ({
    id,
    title,
    content: {} as Record<string, unknown>,
  }));
  const other = sections[SECTION_MAP.findIndex(({ id }) => id === 'other')];
  const add = (key: string, value: unknown, attribute = false) => {
    if (key === '__typename') return;
    const index = SECTION_MAP.findIndex((section) =>
      attribute
        ? section.attributes?.includes(key) ||
          section.prefixes?.some((prefix) => key.startsWith(prefix))
        : section.fields?.includes(key)
    );
    (sections[index] || other).content[attribute ? `attributes.${key}` : key] =
      value;
  };
  for (const [key, value] of Object.entries(task)) {
    if (
      key === 'attributes' &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      for (const [name, attribute] of Object.entries(value))
        add(name, attribute, true);
      if (!Object.keys(value).length) add(key, value);
    } else add(key, value);
  }
  return sections
    .filter((section) => Object.keys(section.content).length)
    .map((section) => {
      // The identity and placement envelopes follow their explicit field order;
      // every nested object (and all other envelopes) is alphabetical.
      const fields = SECTION_MAP.find(
        (entry) => entry.id === section.id
      )?.fields;
      const entries = fields
        ? fields
            .filter((key) => Object.hasOwn(section.content, key))
            .map((key) => [key, sortContent(section.content[key])])
        : Object.entries(
            sortContent(section.content) as Record<string, unknown>
          );
      return { ...section, content: Object.fromEntries(entries) };
    });
}
